/**
 * Org Activation Panel
 * Handles the sales-to-operations handoff: close won/lost, activate org,
 * view sales history, and AI-generated handoff summary.
 */

import React, { useState } from 'react';
import {
  Rocket,
  Building2,
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  Brain,
  Trophy,
  ThumbsDown,
} from 'lucide-react';
import { orgActivationAPI } from '@/services/api/salesPipelineAPI';

interface OrgActivationPanelProps {
  opportunityId: string;
  opportunityStage: string;
  companyName: string;
  companyId?: number;
}

export const OrgActivationPanel: React.FC<OrgActivationPanelProps> = ({
  opportunityId,
  opportunityStage,
  companyName,
  companyId,
}) => {
  const [loading, setLoading] = useState(false);
  const [actionResult, setActionResult] = useState<any>(null);
  const [salesHistory, setSalesHistory] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Activation form
  const [showActivation, setShowActivation] = useState(false);
  const [createNewOrg, setCreateNewOrg] = useState(!companyId);
  const [orgName, setOrgName] = useState(companyName);

  const isWon = opportunityStage === 'closed_won';
  const isLost = opportunityStage === 'closed_lost';
  const isClosed = isWon || isLost;

  const handleActivateOrg = async () => {
    setLoading(true);
    try {
      const result = await orgActivationAPI.activateOrg(opportunityId, {
        orgId: createNewOrg ? undefined : companyId,
        createNewOrg,
        orgName: createNewOrg ? orgName : undefined,
      });
      setActionResult({ type: 'activated', ...result });
      setShowActivation(false);
    } catch (err) {
      console.error('Failed to activate org:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSalesHistory = async () => {
    if (!companyId) return;
    setLoadingHistory(true);
    try {
      const result = await orgActivationAPI.getSalesHistory(companyId);
      setSalesHistory(result);
    } catch (err) {
      console.error('Failed to load sales history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isWon ? 'bg-green-100' : isLost ? 'bg-red-100' : 'bg-blue-100'
        }`}>
          {isWon ? <Trophy className="w-5 h-5 text-green-600" /> :
           isLost ? <ThumbsDown className="w-5 h-5 text-red-500" /> :
           <Rocket className="w-5 h-5 text-blue-600" />}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">
            {isWon ? 'Opportunity Won' : isLost ? 'Opportunity Lost' : 'Opportunity Open'}
          </p>
          <p className="text-xs text-gray-500">
            Stage: {opportunityStage} &bull; {companyName}
          </p>
        </div>
      </div>

      {/* Action Result Banner */}
      {actionResult && (
        <div className={`p-3 rounded-lg text-sm ${
          actionResult.type === 'activated' ? 'bg-green-50 text-green-700 border border-green-200' :
          actionResult.type === 'won' ? 'bg-green-50 text-green-700 border border-green-200' :
          'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <div className="flex items-center gap-2">
            {actionResult.type === 'lost'
              ? <XCircle className="w-4 h-4" />
              : <CheckCircle className="w-4 h-4" />}
            <span className="font-medium">{actionResult.message}</span>
          </div>
          {actionResult.activationSummary && (
            <div className="mt-2 p-2 bg-white rounded border text-xs whitespace-pre-wrap">
              <div className="flex items-center gap-1 font-medium text-purple-700 mb-1">
                <Brain className="w-3 h-3" /> AI Handoff Summary
              </div>
              {typeof actionResult.activationSummary === 'string'
                ? actionResult.activationSummary
                : JSON.stringify(actionResult.activationSummary, null, 2)}
            </div>
          )}
        </div>
      )}

      {/* Guidance for non-closed deals */}
      {!isClosed && !actionResult && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          Use the stage controls in the Details tab to mark this opportunity as Won or Lost.
          Once marked as Won, you can activate the organization here.
        </div>
      )}

      {/* Org Activation (only for closed_won) */}
      {(isWon || actionResult?.type === 'won') && !actionResult?.type?.includes('activated') && (
        <div className="border-2 border-dashed border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-blue-600" />
              <h4 className="text-sm font-semibold text-gray-700">Activate Organization</h4>
            </div>
            <button
              onClick={() => setShowActivation(!showActivation)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showActivation ? 'Cancel' : 'Start Activation'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Transfer sales context, documents, and fee schedule to the organization. AI will generate a handoff summary.
          </p>

          {showActivation && (
            <div className="space-y-3 pt-3 border-t">
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={!createNewOrg && !!companyId} onChange={() => setCreateNewOrg(false)}
                  disabled={!companyId} className="w-4 h-4 text-blue-600" />
                Link to existing org {companyId && `(ID: ${companyId})`}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={createNewOrg} onChange={() => setCreateNewOrg(true)}
                  className="w-4 h-4 text-blue-600" />
                Create new organization
              </label>
              {createNewOrg && (
                <input type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Organization name" className="w-full px-3 py-2 border rounded-md text-sm" />
              )}
              <button onClick={handleActivateOrg} disabled={loading || (createNewOrg && !orgName)}
                className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                Activate & Generate Handoff
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sales History (for activated orgs) */}
      {companyId && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Sales History</h4>
            <button onClick={handleViewSalesHistory} disabled={loadingHistory}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
              {loadingHistory ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Load History
            </button>
          </div>
          {salesHistory && (
            <div className="space-y-3 text-sm">
              {salesHistory.opportunity && (
                <div className="p-2 bg-gray-50 rounded">
                  <span className="text-xs font-medium text-gray-500">Opportunity:</span>
                  <span className="ml-1 text-gray-900">{salesHistory.opportunity.name} - ${salesHistory.opportunity.amount?.toLocaleString()}</span>
                </div>
              )}
              {salesHistory.activationSummary && (
                <div className="p-2 bg-purple-50 rounded">
                  <div className="flex items-center gap-1 text-xs font-medium text-purple-700 mb-1">
                    <Brain className="w-3 h-3" /> Activation Summary
                  </div>
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">{salesHistory.activationSummary}</p>
                </div>
              )}
              {salesHistory.activities && salesHistory.activities.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-gray-500">{salesHistory.activities.length} activities logged</span>
                </div>
              )}
              {salesHistory.documents && salesHistory.documents.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-gray-500">{salesHistory.documents.length} documents</span>
                </div>
              )}
              {salesHistory.feeSchedule && salesHistory.feeSchedule.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-gray-500">
                    Fee schedule: ${salesHistory.feeSchedule.reduce((sum: number, f: any) => sum + (f.amount || 0), 0).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
